import { Box, TextField, Typography, IconButton, InputAdornment } from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import classNames from 'classnames';
import { toastError, toastSuccess } from 'modules/utils';
import React, { FC, forwardRef, memo, useCallback, useMemo, useState } from 'react';
import { useStyles } from './TextInput.styles';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

export type InputFieldProps = {
  label?: string;
  isTextMode?: boolean;
  readOnly?: boolean;
  withCopy?: boolean;
  isCopyOnClick?: boolean;
  withOpen?: boolean;
  isSecret?: boolean;
  value?: string;
  startIcon?: JSX.Element;
  onCopyHandler?: () => void;
} & TextFieldProps;

const TextInputComponent: FC<InputFieldProps> = forwardRef(
  (
    {
      isTextMode,
      isSecret,
      defaultValue,
      readOnly,
      withCopy,
      isCopyOnClick,
      withOpen,
      type = 'text',
      size,
      startIcon,
      onCopyHandler,
      ...rest
    },
    ref
  ) => {
    const { classes } = useStyles({ size });
    const [isSecretVisible, setIsSecretVisible] = useState<boolean>(false);

    const withIcons = useMemo(
      () => withCopy || isSecret || withOpen,
      [withCopy, isSecret, withOpen]
    );

    const copyData = useCallback(
      (value: string) => {
        if (typeof window !== 'undefined') {
          try {
            onCopyHandler?.();
            navigator.clipboard.writeText(value);
            toastSuccess('Content has been copied to clipboard.');
          } catch (error) {
            toastError('Something went wrong!');
          }
        }
      },
      [onCopyHandler]
    );

    const openNewHandler = useCallback((value: string) => window.open(value, '_blank'), []);

    const changeSecretVisibility = useCallback(() => {
      setIsSecretVisible((previousValue) => !previousValue);
    }, []);

    const handleOnClick = useCallback(
      (e: React.SyntheticEvent) => {
        if (!isCopyOnClick) return;

        e.preventDefault();

        copyData(rest?.value as string);
      },
      [copyData, isCopyOnClick, rest?.value]
    );

    return (
      <Box className={classes.root}>
        {isTextMode ? (
          <Typography variant="body1" className={classes.textMode}>
            {rest?.value}
          </Typography>
        ) : (
          <>
            <TextField
              size="small"
              type={isSecret ? (isSecretVisible ? 'text' : 'password') : type}
              className={classNames({
                [classes.readOnly]: readOnly,
                [classes.inputWithIcon]: withIcons,
                [classes.inputWithPointer]: isCopyOnClick,
              })}
              inputRef={ref}
              InputProps={{
                readOnly,
                startAdornment: startIcon && (
                  <InputAdornment position="start">{startIcon}</InputAdornment>
                ),
              }}
              onClick={handleOnClick}
              {...rest}
            />

            {withIcons && (
              <Box className="input-group-append ml-0">
                {isSecret && (
                  <IconButton className={classes.icon} onClick={changeSecretVisibility}>
                    {isSecretVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                )}
                {withCopy && (
                  <IconButton
                    color="primary"
                    className={classes.icon}
                    onClick={() => copyData(rest?.value as string)}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                )}
                {withOpen && (
                  <IconButton
                    color="primary"
                    className={classes.icon}
                    onClick={() => openNewHandler(rest?.value as string)}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    );
  }
);

export const TextInput = memo(TextInputComponent);
